import { useEffect, useRef, useState } from 'react';

import Banner from '@/components/feedback/Banner';
import FormRenderer, { FormRendererRef } from '@/components/FormRenderer';
import { LanguageSelector } from '@/components/LanguageSelector';
import { PortalOutletContext } from '@/components/portal/PortalShellContainer';
import { Button } from '@/components/ui';
import { useAuth } from '@/contexts';
import { useLanguage } from '@/contexts/LanguageContext';
import { checkinApi, CheckInFormResponse } from '@/lib/api';
import { ApiError } from '@/types';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

function CheckInForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasScope } = useAuth();
  const portalOutlet = useOutletContext<PortalOutletContext | null>();
  const refetchPortal = portalOutlet?.refetch;
  const [formData, setFormData] = useState<(CheckInFormResponse & { values?: Record<string, any> }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRendererRef = useRef<FormRendererRef>(null);
  const { language } = useLanguage();

  // Load form data on mount (re-fetches when language changes)
  useEffect(() => {
    let cancelled = false;
    async function loadForm() {
      try {
        setLoading(true);
        setError(null);
        const data = await checkinApi.getForm(language);
        if (cancelled) return;
        setFormData(data);

        // If already submitted, show read-only state
        if (data.submittedAt) {
          setSubmitted(true);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 404) {
            setError('No check-in form is configured for this reservation.');
          } else {
            setError(err.message);
          }
        } else {
          setError('Failed to load check-in form');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadForm();
    return () => {
      cancelled = true;
    };
  }, [language]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData || submitting || submitted || !formRendererRef.current) return;

    setSubmitting(true);
    setError(null);

    try {
      const { ok, values, errors } = formRendererRef.current.collect();

      if (!ok) {
        if (errors.length > 0) {
          // Custom form errors are full sentences (e.g. "Script execution failed");
          // standard form errors are bare field labels (e.g. "Name", "Email").
          setError(
            formData.customHtml
              ? errors.join('; ')
              : `Please fill in: ${errors.join(', ')}`
          );
        } else {
          setError('Please fill in all required fields');
        }
        return;
      }

      await checkinApi.submitForm(values);
      setSubmitted(true);
      void refetchPortal?.();

      // Disable form inputs
      formRendererRef.current.disable();

      // Sequence guests (non-portal) jump back to the checklist. Portal guests
      // stay on the form with the success state and use the sidebar to move on.
      if (!location.pathname.startsWith('/portal/') && hasScope('sequence')) navigate('/pre-arrival-checklist');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'already_submitted') {
          setError('Check-in form already submitted');
          setSubmitted(true);
        } else if (err.code === 'validation_failed') {
          setError(err.message);
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to submit form');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading form...</span>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Form</h3>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No form data available</p>
      </div>
    );
  }

  const isFormDisabled = submitted || formData.submittedAt;

  return (
    <div>
      <LanguageSelector />
      {/* Success message */}
      {submitted && <Banner type="success">Thank you, your check-in form has been submitted.</Banner>}

      {/* Already submitted notice */}
      {formData.submittedAt && !submitted && (
        <Banner type="info">
          Check-in form already submitted on {new Date(formData.submittedAt).toLocaleDateString()}
        </Banner>
      )}

      {/* Error message */}
      {error && <Banner type="error">{error}</Banner>}

      {/* Form content */}
      <div className="mb-8">
        <FormRenderer
          ref={formRendererRef}
          html={formData.markup}
          css={formData.css}
          fieldsSchema={formData.fieldsSchema}
          disabled={!!isFormDisabled}
          initialValues={formData.values}
          customHtml={formData.customHtml}
          submissionScript={formData.submissionScript}
          numOfAdults={formData.numOfAdults}
          maxAccompanyingGuests={formData.maxAccompanyingGuests}
          formComponents={formData.formComponents}
        />
      </div>

      {/* Submit button */}
      {!isFormDisabled && (
        <div className="mt-8">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full rounded-lg h-14 text-base font-medium flex items-center justify-center"
            variant="primary"
            size="lg"
          >
            {submitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Submitting...
              </div>
            ) : (
              'Submit Check-in Form'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default CheckInForm;
